import { getTokenFromLocalStorage, isTokenExpired } from "./tokenUtils";
import { getUserFromLocalStorage } from "./user";

/**
 * Where a signed-in account belongs, and where a signed-out visitor goes.
 *
 * WHY THIS EXISTS
 * ---------------
 * The "which dashboard is yours" decision was written out four times: the
 * operationMode chain at the end of `signin`, the two `router.push`es in its
 * partner and 3PL-staff fallbacks, and again in PartnerActivate and
 * PartnerTwoFAVerify. Nothing consulted any of them outside the login flow,
 * so App.js's catch-all could only do one thing with a URL it did not
 * recognise: send you to the login page.
 *
 * That is the bug this fixes. A 3PL partner signed in at
 * /admin/3pl-partner-dashboard who edited the address bar down to "/" was
 * bounced to the login screen, because "/" matched nothing and the catch-all
 * assumed no-match meant signed-out. It never asked whether anyone was
 * logged in.
 *
 * One resolver, so the answer is the same whether it is asked at login, at
 * the end of a 2FA challenge, or by a bare URL typed into the bar.
 */
export const LOGIN_ROUTE = "/auth/login-page";

const ROUTE_BY_OPERATION_MODE = Object.freeze({
  "3PL_PARTNER": "/admin/3pl-partner-dashboard",
  "3PL_STAFF": "/admin/3pl-partner-dashboard",
  RESTAURANT: "/admin/kitchen",
  HEALTHCARE: "/admin/healthcare-dashboard",
  RETAILER: "/admin/dashboard",
  WHOLESALER: "/admin/dashboard",
});

/**
 * The landing route for one account, given the `result` object that login
 * stores (and that getUserFromLocalStorage returns under `.result`).
 *
 * Everything with no entry of its own, admins and brand owners included,
 * lands on /admin/dashboard. That was already the `else` of the original
 * chain; it stays the fallback rather than becoming a thrown error, because
 * a mode we have not seen is not a reason to strand someone on a blank page.
 */
export const resolveLandingRoute = (result) =>
  ROUTE_BY_OPERATION_MODE[result?.operationMode] || "/admin/dashboard";

/**
 * The landing route for whoever is signed in RIGHT NOW, or null when nobody
 * is. Null means "show the login page", and it is the only signal a caller
 * needs to tell the two cases apart.
 *
 * The token is checked before the profile is read on purpose:
 * getUserFromLocalStorage fires the session-expired toast and its own
 * redirect when it meets an expired token, and a signed-out visitor opening
 * the site should just see the login form, not be told their session ended.
 */
export const resolveSessionLandingRoute = () => {
  const token = getTokenFromLocalStorage();
  if (!token || isTokenExpired(token)) return null;

  const profile = getUserFromLocalStorage();
  if (!profile?.result) return null;

  return resolveLandingRoute(profile.result);
};
